//--- ARQUIVO NOVO: ProductsView.js (Copie e cole tudo) ---
import React, { useState } from 'react';
import { EditIcon, TrashIcon } from './Icons.js';

// Função de máscara de moeda
const formatCurrencyInput = (value) => {
    const numericValue = String(value).replace(/[^\d]/g, '');
    if (!numericValue) return '';
    const number = parseInt(numericValue, 10) / 100;
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(number);
};

// --- Componente do Formulário de Produto/Serviço ---
const ProductForm = ({ onSave, onCancel, product, showNotification }) => {
    const [formData, setFormData] = useState({
        name: product ? product.name : '',
        price: product ? formatCurrencyInput(product.price * 100) : ''
    });
    const [isSaving, setIsSaving] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'price') {
            setFormData(prev => ({ ...prev, [name]: formatCurrencyInput(value) }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const priceNumber = parseFloat(String(formData.price).replace(/\./g, '').replace(',', '.'));

        if (!formData.name.trim() || isNaN(priceNumber) || priceNumber <= 0) {
            showNotification("Preencha o nome e um preço válido.");
            return;
        }
        
        setIsSaving(true);
        try {
            await onSave({ ...product, name: formData.name, price: priceNumber });
        } finally {
            setIsSaving(false);
        }
    };

    const inputClasses = "mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm sm:text-sm dark:bg-gray-700 dark:text-white";

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
                <form onSubmit={handleSubmit}>
                    <div className="p-6">
                        <h3 className="text-xl font-bold dark:text-gray-200 mb-6">{product ? 'Editar Item' : 'Novo Item do Catálogo'}</h3>
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="name" className="dark:text-gray-300">Nome do Produto/Serviço *</label>
                                <input id="name" name="name" type="text" value={formData.name} onChange={handleChange} required placeholder="Ex: Consultoria de Marketing" className={inputClasses} />
                            </div>
                            <div>
                                <label htmlFor="price" className="dark:text-gray-300">Preço Padrão (R$) *</label>
                                <input id="price" name="price" type="text" inputMode="decimal" value={formData.price} onChange={handleChange} required placeholder="150,00" className={inputClasses} />
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 flex justify-end space-x-4 rounded-b-lg">
                        <button type="button" onClick={onCancel} disabled={isSaving} className="px-4 py-2 bg-gray-200 dark:bg-gray-600 dark:text-gray-200 rounded-md">Cancelar</button>
                        <button type="submit" disabled={isSaving} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400">{isSaving ? 'Salvando...' : 'Salvar'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Componente Principal da Tela de Produtos/Serviços ---
export const ProductsView = ({ products, onAdd, onEdit, onDelete, showNotification }) => {
    const [showForm, setShowForm] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);

    const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

    const handleSave = async (productData) => {
        if (productData.id) { await onEdit(productData); } 
        else { await onAdd(productData); }
        setShowForm(false);
        setEditingProduct(null);
    };

    const openFormForNew = () => { setEditingProduct(null); setShowForm(true); };
    const openFormForEdit = (product) => { setEditingProduct(product); setShowForm(true); };
    const confirmDelete = (id) => { if(window.confirm("Tem certeza que deseja excluir este item do catálogo?")) { onDelete(id); } };

    return (
        <>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Produtos & Serviços</h2>
                <button onClick={openFormForNew} className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700">+ Novo Item</button>
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                <th className="px-6 py-3">Item</th>
                                <th className="px-6 py-3">Preço Padrão</th>
                                <th className="px-6 py-3 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {products.length > 0 ? products.map(product => (
                                <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                    <td className="px-6 py-4 whitespace-nowrap"><div className="font-medium text-gray-900 dark:text-gray-200">{product.name}</div></td>
                                    <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900 dark:text-gray-300">{formatCurrency(product.price)}</div></td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button onClick={() => openFormForEdit(product)} className="text-indigo-600 hover:text-indigo-900 mr-4"><EditIcon /></button>
                                        <button onClick={() => confirmDelete(product.id)} className="text-red-600 hover:text-red-900"><TrashIcon /></button>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="3" className="text-center py-10 text-gray-500 dark:text-gray-400">Nenhum produto ou serviço cadastrado.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {showForm && <ProductForm onSave={handleSave} onCancel={() => setShowForm(false)} product={editingProduct} showNotification={showNotification} />}
        </>
    );
};